import { useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { getTheme, type MotifTheme } from '@/motif/setup';
import { ShellHeader } from '@/header/ShellHeader';
import { LoaderOverlay } from '@/ui/LoaderOverlay';
import { ToasterOutlet } from '@/ui/ToasterOutlet';
import { ShellFooter } from '@/ui/ShellFooter';
import { IncidentBanner } from '@/incidents/IncidentBanner';
import { PlatformNotificationToaster } from '@/platform-notifications/PlatformNotificationToaster';
import { SideNavRail } from '@/components/SideNavRail';
import { SessionIdleModal } from '@/auth/SessionIdleModal';
import { SessionCapModal } from '@/auth/SessionCapModal';
import { ClientSwitchBackGuard } from '@/client/ClientSwitchBackGuard';
import { DirectClientChangeGuard } from '@/client/DirectClientChangeGuard';
import { useClientStore, CLIENT_STORAGE_KEYS } from '@/client/client-store';
import { postAppContextChange } from '@/mfe/app-context';
import { setCurrentPage } from '@/legacy/current-page';
import { PopupMfeOverlay } from '@/mfe/PopupMfeOverlay';
import { MfeMountOverlay } from '@/mfe/MfeMountOverlay';
import { useMfeMountStore } from '@/mfe/mfe-mount-store';
import { usePopupUrlSync } from '@/mfe/use-popup-url-sync';
import { idleManager } from '@/auth/idle';
import { sessionDurationManager } from '@/auth/session-duration';
import { msalInstance } from '@/auth/msal';
import { eventsModule } from '@/events/events-module';
import { usePrefetchUserRoles } from '@/header/user-roles';

interface Props {
  children: React.ReactNode;
}

/**
 * Top-level page chrome. Mirrors the legacy `app-layout`
 * (`raptor-shell-ui/src/app/modules/layout/layout.component.html`):
 *
 *   ┌──────────────────────────────────────┐
 *   │ side │  header                       │
 *   │ nav  ├───────────────────────────────┤
 *   │ rail │                               │
 *   │ 60px │  main (router outlet)         │
 *   │      ├───────────────────────────────┤
 *   │      │  footer (Privacy / Terms)     │
 *   └──────┴───────────────────────────────┘
 *
 * Overlays (mounted as siblings of `main`):
 *   - LoaderOverlay  — full-viewport progress loader (M2.6)
 *   - ToasterOutlet  — bottom-right MotifToaster (UI parity)
 */
export function ShellLayout({ children }: Props) {
  const [, setLocal] = useState<MotifTheme>(() => getTheme());
  const location = useRouterState({ select: (s) => s.location });
  const currentClientId = useClientStore((s) => s.current?.id ?? null);
  // Two independent controls (legacy `GlobalNavigationRailState`):
  //   - the header hamburger toggles the rail's VISIBILITY (shown <-> hidden);
  //   - the rail's own bottom chevron EXPANDS/COLLAPSES it (60px icon strip <->
  //     wide labeled panel that overlays the content).
  const [railVisible, setRailVisible] = useState(true);
  const [railExpanded, setRailExpanded] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  // Two-way `?popup=` URL sync: opening a popup MFE reflects it in the URL
  // (shareable / refresh-safe), closing it clears the param.
  usePopupUrlSync();
  usePrefetchUserRoles();

  useEffect(() => {
    const offToggle = eventsModule.on('shell:toggle-sidenav', () => {
      setRailVisible((v) => !v);
      // Showing/hiding the rail changes the content width; nudge charting MFEs
      // (which size to their container at init) to refit so nothing clips.
      const fire = () => window.dispatchEvent(new Event('resize'));
      requestAnimationFrame(fire);
      window.setTimeout(fire, 250);
    });
    const offExpand = eventsModule.on('shell:expand-sidenav', () => setRailExpanded((v) => !v));
    return () => {
      offToggle();
      offExpand();
    };
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => setLocal(getTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['motif-theme'],
    });
    return () => observer.disconnect();
  }, []);

  // Idle-timeout (M2.11) + absolute session-cap managers. Start only when an
  // MSAL active account is present; restart on login event so both timers bind
  // to a real session. The cap enforces `maxSessionDurationHours` (legacy
  // `SessionDurationService`) independent of activity.
  useEffect(() => {
    if (msalInstance.getActiveAccount()) {
      idleManager.start();
      sessionDurationManager.start();
    }
    const sub = msalInstance.addEventCallback?.((evt) => {
      // LOGIN_SUCCESS / SSO_SILENT_SUCCESS / ACQUIRE_TOKEN_SUCCESS — all imply
      // an active account exists. Start (idempotent) on any of them.
      if (
        evt.eventType === 'msal:loginSuccess' ||
        evt.eventType === 'msal:ssoSilentSuccess' ||
        evt.eventType === 'msal:acquireTokenSuccess'
      ) {
        idleManager.start();
        sessionDurationManager.start();
      }
      if (evt.eventType === 'msal:logoutSuccess') {
        idleManager.stop();
        sessionDurationManager.stop();
      }
    });
    return () => {
      idleManager.stop();
      sessionDurationManager.stop();
      if (sub) msalInstance.removeEventCallback?.(sub);
    };
  }, []);

  // Measure the actual header height and publish it as `--shell-header-height`
  // so the fixed header never overlaps or gaps the main content / nav rail,
  // regardless of MotifHeader's intrinsic height or webfont reflow.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--shell-header-height', `${h}px`);
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    // Re-measure once webfonts settle — EYInterstate swapping in can change height.
    document.fonts?.ready?.then(apply).catch(() => {});
    return () => ro.disconnect();
  }, []);

  // App-context sync (legacy `ValidateRouteGuard`): on every client-scoped
  // navigation, write the `CI` current-page key and broadcast APP_CONTEXT_CHANGE
  // to embedded iframes + MFEs so they re-sync to the shell's page + client. The
  // 100ms delay mirrors legacy (lets the destination frame settle).
  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (!segments.includes('app-container')) return;
    const appName = segments[segments.length - 1] || 'home';
    setCurrentPage(appName);
    const clientGuid = currentClientId ?? localStorage.getItem(CLIENT_STORAGE_KEYS.localCurrent);
    const timer = window.setTimeout(() => postAppContextChange(appName, clientGuid), 100);
    return () => window.clearTimeout(timer);
  }, [location.pathname, currentClientId]);

  // Bug #7: the footer must appear only once the Home MFE has fully loaded — not
  // during the transient `/` → `/{clientId}/app-container/home` redirect (where
  // it flashed before any home content). Home mounts at `.../app-container/home`;
  // gate the footer on that path AND the MFE mount store reporting "not loading".
  const mfeLoading = useMfeMountStore((s) => s.loading);
  const onHome = location.pathname.endsWith('/app-container/home');
  const showFooter = location.pathname === '/login' || (onHome && !mfeLoading);

  return (
    <div
      style={
        {
          minHeight: '100vh',
          // Content gutter: 60px while the rail is shown, 0 once the hamburger
          // hides it (content reflows to full width). The Expanded panel overlays
          // the content, so this stays 60px regardless of expand state.
          ['--shell-rail-width']: railVisible ? '60px' : '0px',
        } as React.CSSProperties
      }
    >
      <div
        className={`shell-side-rail${railVisible ? '' : ' shell-side-rail--hidden'}${
          railExpanded ? ' shell-side-rail--expanded' : ''
        }`}
      >
        <SideNavRail expanded={railExpanded} />
      </div>
      <div className="shell-header-bar" ref={headerRef}>
        <ShellHeader navOpen={railVisible} />
      </div>
      <div
        className="shell-content-wrapper"
        style={{
          // Gutter clears the fixed side rail; collapses to 0 when the rail is
          // hidden so the page spans full width (transition matches the rail).
          marginLeft: 'var(--shell-rail-width, 60px)',
          transition: 'margin-left 0.2s ease',
        }}
      >
        <main className="shell-main">{children}</main>
        {showFooter ? <ShellFooter /> : null}
      </div>
      <LoaderOverlay />
      <MfeMountOverlay />
      <ToasterOutlet />
      <IncidentBanner />
      <PlatformNotificationToaster />
      <SessionIdleModal />
      <SessionCapModal />
      <ClientSwitchBackGuard />
      <DirectClientChangeGuard />
      <PopupMfeOverlay />
    </div>
  );
}
